import { useCallback, useEffect, useRef, useState } from "react";
import { Upload03Icon, CheckmarkCircle02Icon, AlertCircleIcon } from "hugeicons-react";
import { useSpaceContext } from "../../../contexts/AppDataContext";
import { CloseButton } from "../../Layout/Buttons/CloseButton";
import { SPACE_FILE_TYPES } from "../../../config/AcceptedFileTypes";
import { chatConfig } from "../../../config/chatConfig";
import { estimateTokensFromChars, estimateTokensFromText } from "../../../helpers/chatHelper";
import useFileHandler from "../../../hooks/FileHandler/useFileHandler";
import FileDrop from "../../Partials/FileDrop";
import { FileCard } from "../../Layout/FileCard";
import { MessageDocument } from "../../../types/message";
import "../../../Styles/Space/Space Page/UploadModal.css";

type UploadStatus = "idle" | "processing" | "uploading" | "success" | "error";

const MAX_TOKENS = chatConfig.limits.tokens.MAX_SPACE_ATTACHMENTS_TOKENS;
const ACCEPTED_EXTENSIONS = Object.values(SPACE_FILE_TYPES).flat();
const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.join(",");
const FILE_TYPES_LABEL = ACCEPTED_EXTENSIONS.map((e) => e.toUpperCase()).join(", ");
const SUCCESS_CLOSE_DELAY_MS = 900;

export const SpaceUploadModal = () => {
  const {
    isUploadModalOpen,
    closeUploadModal,
    currentSpace,
    addTextAttachmentsToSpace,
  } = useSpaceContext();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const uploadedUrlsRef = useRef<Set<string>>(new Set());
  const isUploadingRef = useRef(false);

  const [status, setStatus] = useState<UploadStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const { textFiles, removeTextFile, processFiles, setTextFiles } = useFileHandler({
    messages: [],
    setSalesModalFeature: () => {},
    setShowSalesModal: () => {},
    isSpace: true,
    spaceAttachments: currentSpace?.textAttachments || [],
  });

  const isProcessing = textFiles.some((f) => !f.isLoaded);
  const hasFiles = textFiles.length > 0;
  const isBusy = status === "processing" || status === "uploading";

  const resetState = useCallback(() => {
    uploadedUrlsRef.current = new Set();
    isUploadingRef.current = false;
    setStatus("idle");
    setError(null);
    setTextFiles([]);
  }, [setTextFiles]);

  const handleClose = useCallback(() => {
    if (status === "uploading") return;
    resetState();
    closeUploadModal();
  }, [closeUploadModal, status, resetState]);

  useEffect(() => {
    if (!isUploadModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isUploadModalOpen, handleClose]);

  useEffect(() => {
    if (!isUploadModalOpen) {
      uploadedUrlsRef.current = new Set();
      isUploadingRef.current = false;
      setStatus("idle");
      setError(null);
    }
  }, [isUploadModalOpen]);

  const runUpload = useCallback(
    async (filesToUpload: typeof textFiles) => {
      if (!currentSpace?.id) return;
      if (isUploadingRef.current) return;

      const readyFiles = filesToUpload.filter(
        (f) => f.isLoaded && f.url && !uploadedUrlsRef.current.has(f.url)
      );
      if (!readyFiles.length) return;

      // OpenAI's rule of thumb: 1 token ≈ 4 characters of English text (so
      // 100 tokens ≈ 75 words). We apply it uniformly — using the file's
      // extracted text length when we have it, and falling back to the raw
      // byte size otherwise. One byte ≈ one char for our raw-read fallback,
      // so the same divisor works for both.
      //
      // Special case for scanned / image-only PDFs: pdf.js can't pull text
      // out of rasterized pages, so `content` ends up (near-)empty. The
      // server OCRs those pages and will charge real tokens. Assume ~10%
      // of the file's bytes become extractable text, which at 4 chars/token
      // works out to size / 40.
      const IMAGE_PDF_BYTES_PER_TOKEN = 40;
      const tokensForFile = (f: (typeof readyFiles)[number]): number => {
        const sizeBytes = typeof f.size === "number" ? f.size : 0;
        if (f.type === "pdf") {
          const textTokens = estimateTokensFromText(f.content);
          const ocrFloor = Math.ceil(sizeBytes / IMAGE_PDF_BYTES_PER_TOKEN);
          return Math.max(textTokens, ocrFloor);
        }
        if (f.content) return estimateTokensFromText(f.content);
        return estimateTokensFromChars(sizeBytes);
      };

      const fileTokens = readyFiles.map((f) => tokensForFile(f));
      const addedTokens = fileTokens.reduce((sum, n) => sum + n, 0);
      const projected = (currentSpace.knowledgeCount || 0) + addedTokens;
      if (projected > MAX_TOKENS) {
        // Mark these files as "seen" so the auto-upload effect doesn't
        // immediately retry them and loop on the capacity error.
        readyFiles.forEach((f) => uploadedUrlsRef.current.add(f.url));
        setError("This upload would exceed the space's knowledge capacity.");
        setStatus("error");
        return;
      }

      // Persist `content` only for formats we can truly parse in-browser
      // (docx/pptx/xlsx/plain text/code). PDFs are read locally as raw bytes
      // just so we can token-count them above — the backend does the real
      // extraction from `url` when building space context.
      //
      // `tokens` is persisted per-doc so `removeTextAttachmentFromSpace` can
      // decrement `knowledgeCount` by the same amount we charged on add
      // (critical for PDFs, which have no stored `content`).
      const docs: MessageDocument[] = readyFiles.map((f, i) => ({
        title: f.name,
        url: f.url,
        type: f.type,
        tokens: fileTokens[i],
        ...(f.content && f.type !== "pdf" ? { content: f.content } : {}),
      }));

      isUploadingRef.current = true;
      setError(null);
      setStatus("uploading");

      try {
        await addTextAttachmentsToSpace(currentSpace.id, docs);
        readyFiles.forEach((f) => uploadedUrlsRef.current.add(f.url));
        setStatus("success");
        window.setTimeout(() => {
          resetState();
          closeUploadModal();
        }, SUCCESS_CLOSE_DELAY_MS);
      } catch {
        // Mark these files as "seen" so the auto-upload effect doesn't
        // immediately retry them and loop on the same failure. The user can
        // remove them and re-add to retry.
        readyFiles.forEach((f) => uploadedUrlsRef.current.add(f.url));
        setError("Failed to save files. Please try again.");
        setStatus("error");
      } finally {
        isUploadingRef.current = false;
      }
    },
    [currentSpace?.id, currentSpace?.knowledgeCount, addTextAttachmentsToSpace, closeUploadModal, resetState]
  );

  useEffect(() => {
    if (!isUploadModalOpen) return;
    if (!hasFiles) return;
    if (isProcessing) {
      if (status === "idle") setStatus("processing");
      return;
    }
    // Skip when an upload is in flight, already succeeded, or has errored.
    // For the error case this is what breaks the retry loop — the user must
    // remove the failed file(s) and re-add to try again.
    if (status === "uploading" || status === "success" || status === "error") {
      return;
    }
    const pending = textFiles.filter(
      (f) => f.isLoaded && f.url && !uploadedUrlsRef.current.has(f.url)
    );
    if (!pending.length) return;
    void runUpload(pending);
  }, [isUploadModalOpen, hasFiles, isProcessing, textFiles, status, runUpload]);

  // Swallow file-drop / file-picker events while an upload is in flight so a
  // mid-upload drop isn't silently wiped by the success `resetState()`.
  const handleFilesSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } }) => {
      if (isBusy) return;
      processFiles(event);
    },
    [isBusy, processFiles]
  );

  const openFilePicker = () => {
    if (isBusy) return;
    fileInputRef.current?.click();
  };

  if (!isUploadModalOpen) return null;

  const statusLabel =
    status === "processing"
      ? "Processing files…"
      : status === "uploading"
        ? "Saving to space…"
        : status === "success"
          ? "Uploaded"
          : null;

  return (
    <div
      className="upload-modal-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-modal-title"
    >
      <div
        className="upload-modal-content"
        ref={modalRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="upload-modal-header">
          <h2 id="upload-modal-title">Add Materials</h2>
          <CloseButton onClick={handleClose} />
        </div>

        <div className="upload-modal-body">
          {error && status === "error" && (
            <div className="upload-banner upload-banner-error" role="alert">
              <AlertCircleIcon size={16} />
              <span>{error}</span>
            </div>
          )}

          {statusLabel && (
            <div
              className={`upload-banner ${
                status === "success" ? "upload-banner-success" : "upload-banner-info"
              }`}
              aria-live="polite"
            >
              {status === "success" ? (
                <CheckmarkCircle02Icon size={16} />
              ) : (
                <span className="upload-banner-dots" aria-hidden>
                  <span />
                  <span />
                  <span />
                </span>
              )}
              <span>{statusLabel}</span>
            </div>
          )}

          <div className="upload-form-step">
            {!hasFiles && (
              <p className="upload-form-hint">
                Drop files or click to browse. Supported formats: {FILE_TYPES_LABEL}.
              </p>
            )}

            <FileDrop onFilesDrop={handleFilesSelected}>
              <div
                className={`upload-dropzone ${hasFiles ? "compact" : ""} ${
                  isBusy ? "is-busy" : ""
                }`}
                onClick={openFilePicker}
                role="button"
                tabIndex={0}
                aria-disabled={isBusy}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  className="file-input"
                  onChange={handleFilesSelected}
                  accept={ACCEPT_ATTRIBUTE}
                  multiple
                  disabled={isBusy}
                />
                <Upload03Icon size={hasFiles ? 18 : 24} />
                <span className="upload-dropzone-label">
                  {hasFiles
                    ? isBusy
                      ? "Please wait…"
                      : "Add more files"
                    : "Drop files or click to browse"}
                </span>
                {!hasFiles && (
                  <span className="upload-dropzone-meta">
                    {chatConfig.limits.files.MAX_FILE_SIZE_MB}MB max per file
                  </span>
                )}
              </div>
            </FileDrop>

            {hasFiles && (
              <div className="upload-files-grid">
                {textFiles.map((file) => (
                  <FileCard
                    key={file.id}
                    file={file}
                    onRemove={
                      isBusy ? undefined : (e) => removeTextFile(e, file.id)
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
